import { useState, type ReactNode } from 'react';
import { Combobox, InlineStack, Select } from '@customerio/pluma-components/react';
import { fieldError } from '@customerio/dryad';
import { useBuilderActions, useLeafState, useMeta, useNodeErrors } from '@customerio/dryad-react';
import { EventNameMatchingTooltip } from './event-name-matching-tooltip';
import type { EventNameOption } from './event-name-options';

/**
 * The editing state a behavioral-event leaf holds. A surface's plugin may
 * carry more on its own wire; the row reads only these.
 */
export type EventConditionState = {
  eventName: string;
  inverse: boolean;
};

type EventRowProps = {
  /** The leaf node's id, supplied by the group to every registered view. */
  id: string;
  /** The plugin type gating the typed read — the surface's event leaf. */
  conditionType: string;
  /** The condition-type select, where the surface offers more than one type. */
  typeSelect?: ReactNode;
  /**
   * The event-name picker's options for the term currently typed. The surface
   * owns the endpoint and the debounce; this row only renders what comes back.
   * A hook, so the surface can fetch — named `use…` at the call site and
   * called unconditionally here.
   */
  useEventNameOptions: (term: string) => EventNameOption[];
};

const INVERSE_OPTIONS = [
  { value: 'performed', label: 'has been performed' },
  { value: 'not-performed', label: 'has not been performed' },
];

/**
 * A behavioral event condition row: [type] [event name] [has (not) been
 * performed]. The state's `eventType`/`human` fields have no controls: they
 * only ride along so a parsed `p-` page bitmap or `_h`-suffixed name
 * re-serializes byte-identically.
 */
export function EventRow({ id, conditionType, typeSelect, useEventNameOptions }: EventRowProps) {
  const meta = useMeta();
  const { state, node } = useLeafState<EventConditionState>(id, conditionType);
  const { updateState } = useBuilderActions();
  const errors = useNodeErrors(id);

  // The combobox's live input text, kept local so keystrokes filter
  // suggestions without a builder round-trip.
  const [inputValue, setInputValue] = useState(() => state.eventName);
  const searchableEvents = useEventNameOptions(inputValue);

  // "Event name can't be blank." renders only once the input has been visited
  // (blur) — or from the start when the row mounted with a name already set —
  // so a freshly added row doesn't open covered in red.
  const [eventNameTouched, setEventNameTouched] = useState(() => state.eventName !== '');
  const eventNameErrorMessage = eventNameTouched ? fieldError(errors, 'eventName') : undefined;

  const setEventName = (eventName: string) => {
    setInputValue(eventName);
    if (node != null) {
      updateState(node, { eventName });
    }
  };

  return (
    <InlineStack data-testid="dryad-condition-row" gap="050" alignBlock="baseline" wrap>
      {typeSelect}
      <EventNameMatchingTooltip eventName={state.eventName}>
        <Combobox
          data-testid="dryad-behavioral-event-name"
          ariaLabel="Event name"
          size="sm"
          inputValue={inputValue}
          selectedValue={state.eventName === '' ? null : state.eventName}
          onValueChange={setEventName}
          onSelectionChange={(value: string | null) => {
            if (value != null) {
              setEventName(value);
            }
          }}
          onBlur={() => setEventNameTouched(true)}
          options={searchableEvents}
          placeholder="Select an event"
          error={eventNameErrorMessage}
          withVirtualizer
          shouldFilter={false}
          isDisabled={meta.isDisabled}
        />
      </EventNameMatchingTooltip>
      <Select
        data-testid="dryad-behavioral-event-inverse-select"
        ariaLabel="Event occurrence"
        size="sm"
        options={INVERSE_OPTIONS}
        value={state.inverse ? 'not-performed' : 'performed'}
        onChange={(value: string | null) => {
          if (value != null && node != null) {
            updateState(node, { inverse: value === 'not-performed' });
          }
        }}
        isDisabled={meta.isDisabled}
      />
    </InlineStack>
  );
}
